// ============================================
// Security Audit Log Viewer
//
// Auditor-safe view of security/audit logs.
// Sensitive fields (PII, PHI, IP, actor identity)
// are redacted before display. Accessible to the
// auditor role via admin navigation.
// ============================================

import { useState, useEffect, useMemo } from 'react';
import { Shield, Search, ChevronDown, ChevronUp } from 'lucide-react';
import { useAdminAuth } from '../../../context/AdminAuthContext';
import { getAuditLog } from '../../../services/firestore/dataopsService';
import { redactAuditEntries } from '../../../utils/auditLogRedaction';
import type { AuditLogEntry, AuditAction } from '../../../types/dataops';

const C = {
  text: '#18181B', textSec: '#52525B', textMuted: '#A1A1AA',
  rose: '#E11D48', pass: '#059669', warn: '#B45309', fail: '#E11D48',
  bgCard: '#FFFFFF', border: '#E4E4E7', bg: '#FAFAFA',
};

const ACTION_CATEGORIES: Record<string, string[]> = {
  'Authentication': ['auth.login', 'auth.logout', 'auth.failed', 'auth.password_change', 'auth.mfa_enabled'],
  'Data Access': ['data.view', 'data.export', 'data.modify', 'data.delete', 'data.share'],
  'PHI Operations': ['phi.access', 'phi.transmit', 'phi.disclose'],
  'Safety Events': ['safety.exclusion_applied', 'safety.protocol_regenerated', 'safety.validation_passed', 'safety.validation_failed', 'safety.anomaly_detected', 'safety.anomaly_alert_sent'],
  'Consent': ['consent.granted', 'consent.withdrawn'],
  'Admin Actions': ['admin.user_view', 'admin.user_modify', 'admin.request_process', 'admin.export_generated'],
  'Operations': ['ops.remediation_executed', 'ops.alert_acknowledged', 'ops.alert_resolved', 'ops.secret_rotated', 'ops.failover_activated', 'ops.failback_completed'],
};

const RESULT_COLORS: Record<string, { bg: string; text: string }> = {
  success: { bg: '#D1FAE5', text: '#059669' },
  denied: { bg: '#FEF3C7', text: '#B45309' },
  error: { bg: '#FFF1F2', text: '#E11D48' },
};

export function SecurityAuditLogViewer() {
  const { role } = useAdminAuth();
  const isAuditor = role === 'auditor';
  const [entries, setEntries] = useState<AuditLogEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filterCategory, setFilterCategory] = useState<string>('all');
  const [filterResult, setFilterResult] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedId, setExpandedId] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const raw = await getAuditLog(500);
        // Auditors always get redacted entries; other admin roles see redacted too
        // because this viewer is purpose-built for auditing without sensitive exposure
        setEntries(redactAuditEntries(raw));
      } catch (err) {
        console.error('Failed to load audit log:', err);
        setError('Failed to load audit log entries.');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const filtered = useMemo(() => {
    return entries.filter((e) => {
      if (filterResult !== 'all' && e.result !== filterResult) return false;
      if (filterCategory !== 'all') {
        const actions = ACTION_CATEGORIES[filterCategory];
        if (actions && !actions.includes(e.action)) return false;
      }
      if (searchTerm) {
        const term = searchTerm.toLowerCase();
        return (
          e.action.toLowerCase().includes(term) ||
          e.resource.toLowerCase().includes(term) ||
          e.result.toLowerCase().includes(term)
        );
      }
      return true;
    });
  }, [entries, filterCategory, filterResult, searchTerm]);

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: 60, color: C.textMuted }}>
        Loading audit log...
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ textAlign: 'center', padding: 60, color: C.fail }}>
        {error}
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 1200, margin: '0 auto' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
        <Shield size={20} style={{ color: C.rose }} />
        <h2 style={{ fontSize: 18, fontWeight: 600, color: C.text, margin: 0, fontFamily: "'Instrument Serif', serif" }}>
          Security Audit Log
        </h2>
        {isAuditor && (
          <span style={{
            fontSize: 11, fontWeight: 600, textTransform: 'uppercase',
            background: '#EEF2FF', color: '#6366F1',
            padding: '2px 8px', borderRadius: 4,
          }}>
            Auditor View — Sensitive Data Redacted
          </span>
        )}
      </div>

      {/* Filters */}
      <div style={{
        display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center',
      }}>
        <div style={{ position: 'relative', flex: '1 1 200px', maxWidth: 300 }}>
          <Search size={14} style={{ position: 'absolute', left: 10, top: 10, color: C.textMuted }} />
          <input
            type="text"
            placeholder="Search by action or resource..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              width: '100%', padding: '8px 8px 8px 30px', fontSize: 13,
              border: `1px solid ${C.border}`, borderRadius: 4,
              fontFamily: "'DM Sans', sans-serif", color: C.text,
              background: C.bgCard,
            }}
          />
        </div>

        <select
          value={filterCategory}
          onChange={(e) => setFilterCategory(e.target.value)}
          style={{
            padding: '8px 12px', fontSize: 13, borderRadius: 4,
            border: `1px solid ${C.border}`, color: C.text,
            fontFamily: "'DM Sans', sans-serif", background: C.bgCard,
          }}
        >
          <option value="all">All Categories</option>
          {Object.keys(ACTION_CATEGORIES).map((cat) => (
            <option key={cat} value={cat}>{cat}</option>
          ))}
        </select>

        <select
          value={filterResult}
          onChange={(e) => setFilterResult(e.target.value)}
          style={{
            padding: '8px 12px', fontSize: 13, borderRadius: 4,
            border: `1px solid ${C.border}`, color: C.text,
            fontFamily: "'DM Sans', sans-serif", background: C.bgCard,
          }}
        >
          <option value="all">All Results</option>
          <option value="success">Success</option>
          <option value="denied">Denied</option>
          <option value="error">Error</option>
        </select>

        <span style={{ fontSize: 13, color: C.textMuted }}>
          {filtered.length} of {entries.length} entries
        </span>
      </div>

      {/* Log Table */}
      <div style={{
        background: C.bgCard, border: `1px solid ${C.border}`,
        borderRadius: 4, overflow: 'hidden',
      }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ borderBottom: `1px solid ${C.border}` }}>
              <Th>Timestamp</Th>
              <Th>Action</Th>
              <Th>Resource</Th>
              <Th>Actor</Th>
              <Th>Result</Th>
              <Th style={{ width: 40 }}></Th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr>
                <td colSpan={6} style={{ textAlign: 'center', padding: 40, color: C.textMuted, fontSize: 14 }}>
                  No entries match current filters.
                </td>
              </tr>
            ) : filtered.map((entry) => {
              const isExpanded = expandedId === entry.id;
              const resultStyle = RESULT_COLORS[entry.result] || RESULT_COLORS.error;
              return (
                <EntryRow
                  key={entry.id}
                  entry={entry}
                  isExpanded={isExpanded}
                  resultStyle={resultStyle}
                  onToggle={() => setExpandedId(isExpanded ? null : entry.id)}
                />
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Redaction notice */}
      <div style={{
        marginTop: 16, padding: '12px 16px',
        background: '#EEF2FF', border: '1px solid #C7D2FE',
        borderRadius: 4, fontSize: 13, color: '#4338CA',
      }}>
        <strong>Redaction policy:</strong> Actor identities, IP addresses, user agents,
        and fields containing PII/PHI are automatically redacted in this view.
        Unredacted logs are available to system owner via direct Firestore access
        with full audit trail of who accessed them.
      </div>
    </div>
  );
}

function EntryRow({
  entry, isExpanded, resultStyle, onToggle,
}: {
  entry: AuditLogEntry;
  isExpanded: boolean;
  resultStyle: { bg: string; text: string };
  onToggle: () => void;
}) {
  const hasDetails = Object.keys(entry.details).length > 0;
  const ExpandIcon = isExpanded ? ChevronUp : ChevronDown;

  return (
    <>
      <tr
        style={{
          borderBottom: `1px solid ${C.border}`,
          cursor: hasDetails ? 'pointer' : 'default',
        }}
        onClick={hasDetails ? onToggle : undefined}
      >
        <td style={{ padding: '10px 12px', fontSize: 13, fontFamily: "'JetBrains Mono', monospace", color: C.textSec, whiteSpace: 'nowrap' }}>
          {formatTimestamp(entry.timestamp)}
        </td>
        <td style={{ padding: '10px 12px', fontSize: 13, fontWeight: 500, color: C.text }}>
          {entry.action}
        </td>
        <td style={{ padding: '10px 12px', fontSize: 13, color: C.textSec, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {entry.resource}
        </td>
        <td style={{ padding: '10px 12px', fontSize: 13, fontFamily: "'JetBrains Mono', monospace", color: C.textMuted }}>
          {entry.actor}
        </td>
        <td style={{ padding: '10px 12px' }}>
          <span style={{
            display: 'inline-block', fontSize: 11, fontWeight: 600,
            textTransform: 'uppercase', padding: '2px 8px', borderRadius: 4,
            background: resultStyle.bg, color: resultStyle.text,
          }}>
            {entry.result}
          </span>
        </td>
        <td style={{ padding: '10px 12px' }}>
          {hasDetails && <ExpandIcon size={14} style={{ color: C.textMuted }} />}
        </td>
      </tr>
      {isExpanded && hasDetails && (
        <tr style={{ borderBottom: `1px solid ${C.border}` }}>
          <td colSpan={6} style={{ padding: '12px 24px', background: C.bg }}>
            <div style={{ fontSize: 12, fontWeight: 600, textTransform: 'uppercase', color: C.textMuted, marginBottom: 8 }}>
              Details (redacted)
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '4px 16px' }}>
              {Object.entries(entry.details).map(([key, value]) => (
                <DetailRow key={key} label={key} value={String(value)} />
              ))}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

function DetailRow({ label, value }: { label: string; value: string }) {
  const isRedacted = value === '[REDACTED]';
  return (
    <>
      <span style={{ fontSize: 13, fontFamily: "'JetBrains Mono', monospace", color: C.textMuted }}>
        {label}:
      </span>
      <span style={{
        fontSize: 13,
        fontFamily: "'JetBrains Mono', monospace",
        color: isRedacted ? '#A78BFA' : C.textSec,
        fontStyle: isRedacted ? 'italic' : 'normal',
      }}>
        {value}
      </span>
    </>
  );
}

function formatTimestamp(ts: string): string {
  try {
    const d = new Date(ts);
    return d.toLocaleString('en-US', {
      month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit', second: '2-digit',
      hour12: false,
    });
  } catch {
    return ts;
  }
}

function Th({ children, style }: { children?: React.ReactNode; style?: React.CSSProperties }) {
  return (
    <th style={{
      textAlign: 'left', fontSize: 12, fontWeight: 600, textTransform: 'uppercase',
      letterSpacing: 0.5, color: '#A1A1AA', padding: '8px 12px',
      borderBottom: '1px solid #E4E4E7', ...style,
    }}>
      {children}
    </th>
  );
}
